import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAdminOrder } from '@/hooks/useAdmin'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FulfillmentForm } from '@/components/admin/fulfillment-form'
import { callEdgeFunction } from '@/lib/edge-functions'
import {
  ArrowLeft,
  Package,
  Loader2,
  ExternalLink,
  Mail,
  Phone,
  User,
  Copy,
  Check,
  Clock,
  CreditCard,
  Truck,
  Home,
  Archive,
  ArchiveRestore,
  Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'
import type { FulfillmentStatus, ProcurementStatus, ShippingAddress, OrderItem } from '@/types/database'

const statusColors: Record<FulfillmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
}

const procurementColors: Record<ProcurementStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  ordered_from_vendor: 'bg-blue-100 text-blue-800',
  vendor_shipped: 'bg-purple-100 text-purple-800',
  received_tracking: 'bg-green-100 text-green-800',
  issue: 'bg-red-100 text-red-800',
}

const procurementLabels: Record<ProcurementStatus, string> = {
  pending: 'Pending',
  ordered_from_vendor: 'Ordered',
  vendor_shipped: 'Vendor Shipped',
  received_tracking: 'Tracking Received',
  issue: 'Issue',
}

function parseCustomInputValue(value: string): { url: string; fileName: string } | null {
  if (value.includes('||')) {
    const [url, fileName] = value.split('||')
    return { url, fileName }
  }
  return null
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      title={`Copy ${label || text}`}
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}

function AdminOrderItemRow({ item }: { item: OrderItem }) {
  const customInputs = item.custom_inputs as Record<string, string> | null

  return (
    <div className="py-4 border-b last:border-b-0">
      <div className="flex gap-4">
        <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {item.product_thumbnail_url ? (
            <img
              src={item.product_thumbnail_url}
              alt={item.product_title || 'Product'}
              className="h-full w-full object-cover"
            />
          ) : (
            <Package className="h-6 w-6 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium">{item.product_title || 'Product'}</p>

          <div className="flex flex-wrap items-center gap-2 mt-1">
            {item.variant_label && (
              <Badge variant="secondary" className="text-xs">
                {item.variant_label}
              </Badge>
            )}
            {item.variant_sku && (
              <span className="text-xs text-muted-foreground font-mono">
                SKU: {item.variant_sku}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span>x{item.quantity}</span>
            {item.discount_percent > 0 && (
              <Badge variant="outline" className="text-xs text-red-600 border-red-200">
                -{item.discount_percent}%
              </Badge>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          {item.discount_percent > 0 && (
            <p className="text-sm text-muted-foreground line-through">
              ${(Number(item.unit_price) * item.quantity).toFixed(2)}
            </p>
          )}
          <p className="font-medium">${Number(item.line_total).toFixed(2)}</p>
        </div>
      </div>

      {/* Custom inputs */}
      {customInputs && Object.keys(customInputs).length > 0 && (
        <div className="mt-3 ml-20 space-y-1">
          {Object.entries(customInputs).map(([label, value]) => {
            const upload = parseCustomInputValue(value)
            return (
              <div key={label} className="text-xs text-muted-foreground">
                <span className="font-medium">{label}: </span>
                {upload ? (
                  <a
                    href={upload.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {upload.fileName}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span>{value}</span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Procurement info */}
      <div className="mt-3 ml-20 flex flex-wrap items-center gap-2">
        <Badge className={procurementColors[item.procurement_status]}>
          {procurementLabels[item.procurement_status]}
        </Badge>
        {item.procurement_notes && (
          <span className="text-xs text-muted-foreground">{item.procurement_notes}</span>
        )}
        {item.manufacturer_order_id && (
          <span className="text-xs text-muted-foreground font-mono">
            Vendor #{item.manufacturer_order_id}
          </span>
        )}
        {item.vendor_tracking_number && (
          <span className="text-xs text-muted-foreground font-mono">
            Track: {item.vendor_tracking_number}
          </span>
        )}
      </div>
    </div>
  )
}

function TimelineEvent({ icon: Icon, label, date, active }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  date?: string | null
  active?: boolean
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={`flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 ${
        active ? 'bg-primary text-primary-foreground' : date ? 'bg-muted text-foreground' : 'bg-muted/50 text-muted-foreground'
      }`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <p className={`text-sm font-medium ${active ? 'text-primary' : date ? 'text-foreground' : 'text-muted-foreground'}`}>
          {label}
        </p>
        {date && (
          <p className="text-xs text-muted-foreground">
            {format(new Date(date), 'MMM d, yyyy h:mm a')}
          </p>
        )}
      </div>
    </div>
  )
}

export function AdminOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { order, orderItems, isLoading, updateOrder } = useAdminOrder(id)
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!order) {
    return (
      <div>
        <Link to="/admin/orders" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Link>
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Order not found</h3>
        </div>
      </div>
    )
  }

  const shippingAddress = order.shipping_address as ShippingAddress | null
  const hasItems = orderItems.length > 0
  const itemsSubtotal = hasItems
    ? orderItems.reduce((sum, item) => sum + Number(item.line_total), 0)
    : Number(order.total_amount)

  const stripePaymentUrl = order.stripe_payment_intent_id
    ? `https://dashboard.stripe.com/payments/${order.stripe_payment_intent_id}`
    : null

  const handleResendConfirmation = async () => {
    setIsResendingConfirmation(true)
    try {
      const result = await callEdgeFunction('send-order-confirmation', {
        method: 'POST',
        body: { order_id: order.id },
        withAuth: true,
      })

      if (result.error) {
        toast.error(`Failed to resend confirmation: ${result.error}`)
        return
      }

      toast.success('Order confirmation email resent')
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Failed to resend confirmation: ${error.message}`
          : 'Failed to resend confirmation'
      )
    } finally {
      setIsResendingConfirmation(false)
    }
  }

  const handleArchiveToggle = async () => {
    setIsArchiving(true)
    try {
      const newValue = !order.is_archived
      const { error } = await updateOrder({ is_archived: newValue })
      if (error) throw new Error(error)
      toast.success(newValue ? 'Order archived' : 'Order unarchived')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update order'
      )
    } finally {
      setIsArchiving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      // Delete order_items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', id)
      if (itemsError) throw itemsError

      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', id)
      if (error) throw error

      toast.success('Order permanently deleted')
      navigate('/admin/orders?view=archived')
    } catch (error) {
      console.error('Delete order error:', error)
      toast.error('Failed to delete order')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Order #{order.id.slice(0, 8)} | Admin</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        <Link to="/admin/orders" className="inline-flex items-center text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Order #{order.id.slice(0, 8)}</h1>
            <p className="text-muted-foreground mt-1">
              {format(new Date(order.created_at), 'MMMM d, yyyy h:mm a')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResendConfirmation}
              disabled={isResendingConfirmation}
            >
              {isResendingConfirmation && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Resend confirmation
            </Button>
            <Badge className={statusColors[order.fulfillment_status]}>
              {order.fulfillment_status}
            </Badge>
          </div>
        </div>

        {/* Shopify-style 2-column layout */}
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          {/* LEFT COLUMN: Items + Payment + Fulfillment + Timeline */}
          <div className="space-y-6">
            {/* Items Ordered */}
            <Card>
              <CardHeader>
                <CardTitle>Items Ordered</CardTitle>
              </CardHeader>
              <CardContent>
                {hasItems ? (
                  <div className="divide-y">
                    {orderItems.map((item) => (
                      <AdminOrderItemRow key={item.id} item={item} />
                    ))}
                  </div>
                ) : (
                  // Legacy single-product fallback
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {order.product?.video_thumbnail_url ? (
                        <img
                          src={order.product.video_thumbnail_url}
                          alt={order.product.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{order.product?.title || 'Product'}</p>
                      <p className="text-sm text-muted-foreground">Qty: {order.quantity}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Payment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Subtotal ({hasItems ? orderItems.length : 1} item{(hasItems ? orderItems.length : 1) !== 1 ? 's' : ''})
                  </span>
                  <span>${itemsSubtotal.toFixed(2)}</span>
                </div>
                {order.total_savings && Number(order.total_savings) > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Total Savings</span>
                    <span>-${Number(order.total_savings).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-2 border-t text-base">
                  <span>Total</span>
                  <span>${Number(order.total_amount).toFixed(2)}</span>
                </div>
                {stripePaymentUrl && (
                  <div className="pt-2">
                    <a
                      href={stripePaymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <CreditCard className="h-3 w-3" />
                      View in Stripe
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Fulfillment */}
            <Card>
              <CardHeader>
                <CardTitle>Fulfillment</CardTitle>
              </CardHeader>
              <CardContent>
                <FulfillmentForm order={order} />
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <TimelineEvent
                    icon={Clock}
                    label="Order placed"
                    date={order.created_at}
                    active={order.fulfillment_status === 'pending'}
                  />
                  {(order.fulfillment_status === 'processing' ||
                    order.fulfillment_status === 'shipped' ||
                    order.fulfillment_status === 'delivered') && (
                    <TimelineEvent
                      icon={Package}
                      label="Marked as processing"
                      date={order.created_at}
                      active={order.fulfillment_status === 'processing'}
                    />
                  )}
                  {(order.fulfillment_status === 'shipped' ||
                    order.fulfillment_status === 'delivered') && (
                    <TimelineEvent
                      icon={Truck}
                      label={`Shipped${order.tracking_number ? ` — ${order.tracking_number}` : ''}`}
                      date={order.shipped_at}
                      active={order.fulfillment_status === 'shipped'}
                    />
                  )}
                  {order.fulfillment_status === 'delivered' && (
                    <TimelineEvent
                      icon={Home}
                      label="Delivered"
                      date={order.delivered_at}
                      active
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT SIDEBAR: Customer + Address + Notes + Metadata */}
          <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            {/* Customer */}
            <Card>
              <CardHeader>
                <CardTitle>Customer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {order.customer_name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm">{order.customer_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <a
                    href={`mailto:${order.customer_email}`}
                    className="text-sm text-primary hover:underline truncate"
                  >
                    {order.customer_email}
                  </a>
                </div>
                {order.customer_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <a
                      href={`tel:${order.customer_phone}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {order.customer_phone}
                    </a>
                  </div>
                )}
                {order.user_id ? (
                  <p className="text-xs text-muted-foreground">Registered customer</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Guest checkout</p>
                )}
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card>
              <CardHeader>
                <CardTitle>Shipping Address</CardTitle>
              </CardHeader>
              <CardContent>
                {shippingAddress ? (
                  <div className="text-sm space-y-0.5">
                    <p className="font-medium">{shippingAddress.name}</p>
                    <p className="text-muted-foreground">{shippingAddress.line1}</p>
                    {shippingAddress.line2 && (
                      <p className="text-muted-foreground">{shippingAddress.line2}</p>
                    )}
                    <p className="text-muted-foreground">
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                    </p>
                    <p className="text-muted-foreground">{shippingAddress.country}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No shipping address</p>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.admin_notes ? (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Admin Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{order.admin_notes}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No admin notes. Use the fulfillment form to add notes.
                  </p>
                )}
                {order.customer_notes && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Customer Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{order.customer_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Metadata */}
            <Card>
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Order ID</span>
                  <div className="flex items-center gap-1">
                    <code className="text-xs font-mono">{order.id.slice(0, 8)}</code>
                    <CopyButton text={order.id} label="Order ID" />
                  </div>
                </div>
                {order.stripe_session_id && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Stripe Session</span>
                    <div className="flex items-center gap-1">
                      <code className="text-xs font-mono">{order.stripe_session_id.slice(0, 12)}...</code>
                      <CopyButton text={order.stripe_session_id} label="Stripe Session ID" />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Order Date</span>
                  <span className="text-xs">{format(new Date(order.created_at), 'MMM d, yyyy h:mm a')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Item Count</span>
                  <span className="text-xs">{order.item_count}</span>
                </div>
              </CardContent>
            </Card>

            {/* Archive / Delete Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleArchiveToggle}
                  disabled={isArchiving}
                >
                  {isArchiving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : order.is_archived ? (
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                  ) : (
                    <Archive className="mr-2 h-4 w-4" />
                  )}
                  {order.is_archived ? 'Unarchive Order' : 'Archive Order'}
                </Button>
                {order.is_archived && (
                  <>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Order
                    </Button>
                    <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Delete order permanently?</DialogTitle>
                          <DialogDescription>
                            This action cannot be undone. Order data, items, and history will be permanently removed.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting && <Loader2 className="mr-2 size-4 animate-spin" />}
                            Delete Permanently
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
